// ABOUTME: Dialog to capture title + customer info when converting a cart into a draft offer.
// ABOUTME: On submit, converts the cart and navigates to the resulting offer detail.

import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@ds/coolshop';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useCartContext } from './CartContext';

interface ConvertCartDialogProps {
    open: boolean;
    onClose: () => void;
}

export function ConvertCartDialog({ open, onClose }: ConvertCartDialogProps) {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const cart = useCartContext();

    const [title, setTitle] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleConvert = async () => {
        if (!cart) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await cart.convert({
                title,
                customerName,
                customerEmail: customerEmail || undefined,
            });
            setTitle('');
            setCustomerName('');
            setCustomerEmail('');
            onClose();
            cart.closeDrawer();
            navigate(`/offers/${result.offerId}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to convert cart');
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit = title.trim().length > 0 && customerName.trim().length > 0 && !submitting;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {formatMessage({
                    id: 'cpq.cart.convert.title',
                    defaultMessage: 'Convert cart to offer',
                })}
            </DialogTitle>
            <DialogContent>
                <Stack gap={2} mt={1}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        label={formatMessage({
                            id: 'cpq.cart.convert.offerTitle',
                            defaultMessage: 'Offer title',
                        })}
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        fullWidth
                        required
                    />
                    <TextField
                        label={formatMessage({
                            id: 'cpq.cart.convert.customerName',
                            defaultMessage: 'Customer name',
                        })}
                        value={customerName}
                        onChange={e => setCustomerName(e.target.value)}
                        fullWidth
                        required
                    />
                    <TextField
                        label={formatMessage({
                            id: 'cpq.cart.convert.customerEmail',
                            defaultMessage: 'Customer email',
                        })}
                        value={customerEmail}
                        onChange={e => setCustomerEmail(e.target.value)}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Box display="flex" gap={1}>
                    <Button variant="text" onClick={onClose} disabled={submitting}>
                        {formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button variant="contained" onClick={handleConvert} disabled={!canSubmit}>
                        {formatMessage({
                            id: 'cpq.cart.convert.confirm',
                            defaultMessage: 'Create offer',
                        })}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
